import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Line } from 'react-chartjs-2'
import { injectIntl } from 'react-intl'
import WidgetContainer from 'components/Apps/WidgetContainer'
import messages from 'components/Apps/SlaMonitor/translate'
import { getChartsRatio } from 'helpers/slaMonitor'
import ifWindowWidthLessThan from 'helpers/ifWindowWidthLessThan'
import { toFixed } from 'helpers/math'
import { resetCageSFRData } from 'reducers/slaMonitor/reducer'
import CircleLoader from 'components/Loading/CircleLoader'

class CageSFRCorrected extends Component {
  componentWillUnmount() {
    const { resetCageSFRData } = this.props
    resetCageSFRData()
  }

  chartData = () => {
    const values = this.props.cageSFRData.get('data')
    const labels = []
    const dataCorrected = []
    const dataExpected = []
    const dataObserved = []

    for (const key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) {
        labels.push(values[key].date)
        dataCorrected.push(Math.round(values[key].sfr_corrected * 100))
        dataExpected.push(values[key].sfr_expected.toFixed(2))
        dataObserved.push(values[key].sfr_observed.toFixed(2))
      }
    }

    return {
      labels,
      datasets: [
        {
          lineTension: 0,
          label: 'Observed',
          yAxisID: 'right',
          fill: false,
          backgroundColor: '#273238',
          borderColor: '#273238',
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data: dataObserved,
        },
        {
          lineTension: 0,
          label: 'Expected',
          yAxisID: 'right',
          fill: false,
          backgroundColor: '#80a9bb',
          borderColor: '#80a9bb',
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data: dataExpected,
        },
        {
          lineTension: 0,
          label: 'Corrected',
          yAxisID: 'left',
          fill: false,
          backgroundColor: '#4dbdd5',
          borderColor: '#4dbdd5',
          pointRadius: 3,
          pointHitRadius: 3,
          pointHoverRadius: 3,
          data: dataCorrected,
        },
      ],
    }
  }

  chartOptions = () => {
    const {
      intl: { formatMessage },
    } = this.props
    const data = this.getDatasetLabels()
    let min = 0
    let max = 0

    if (data.length > 0) {
      min = Math.floor(Math.min(...data) * 10) / 10
      max = Math.ceil(Math.max(...data) * 10) / 10
    }

    return {
      aspectRatio: getChartsRatio(),
      responsive: true,
      legend: {
        display: true,
      },
      scales: {
        xAxes: [
          {
            stacked: true,
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.date),
            },
            offset: true,
            ticks: {
              beginAtZero: false,
              autoSkipPadding: 20,
            },
            gridLines: {
              drawOnChartArea: true,
            },
          },
        ],
        yAxes: [
          {
            id: 'left',
            position: 'left',
            type: 'linear',
            ticks: {
              min: min * 10 * 10,
              max: max * 10 * 10,
              stepSize: 20,
              callback: value => `${value} %`,
            },
            scaleLabel: {
              display: true,
              labelString: formatMessage(messages.sfrCorrected),
            },
          },
          {
            id: 'right',
            position: 'right',
            type: 'linear',
            ticks: {
              min,
              max,
              stepSize: 0.2,
            },
            scaleLabel: {
              display: true,
              labelString: `${formatMessage(messages.observedExpected)}`,
            },
          },
        ],
      },
      tooltips: {
        enabled: true,
      },
      plugins: {
        datalabels: {
          anchor: 'start',
          color: 'black',
          align(context) {
            const index = context.dataIndex
            const value = context.dataset.data[index]

            return value < 10 ? 'end' : 'start'
          },
          offset: 1,
          labels: {
            value: {
              font: {
                weight: 'bold',
                size: ifWindowWidthLessThan(1280) ? 9 : 11,
              },
            },
          },
          formatter: () => '',
        },
      },
    }
  }

  getDatasetLabels = () => {
    const data = this.props.cageSFRData.get('data')
    const labels = []

    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        labels.push(Math.round(data[key].sfr_corrected * 100) / 100)
        labels.push(Math.round(data[key].sfr_expected * 100) / 100)
        labels.push(Math.round(data[key].sfr_observed * 100) / 100)
      }
    }

    return labels.sort()
  }

  mathRound = value => Math.round(value * 100) / 100

  getSideContent = () => {
    const {
      intl: { formatMessage },
      siteLevel,
      goBack,
      cageSFRData,
    } = this.props

    if (ifWindowWidthLessThan(992) && siteLevel !== true) {
      return (
        <button type="button" className="btn-link" onClick={goBack}>
          {formatMessage(messages.back)}
        </button>
      )
    }

    return cageSFRData.get('last_updated')
  }

  render() {
    const {
      cageSFRData,
      chartLoader,
      intl: { formatMessage },
    } = this.props

    return (
      <WidgetContainer
        name={{
          start: formatMessage(messages.sfrCorrected),
          end: `${formatMessage(messages.cage)} ${cageSFRData.get(
            'locus_name',
          )}`,
        }}
        sideTitle={this.getSideContent()}
        className="feed-module">
        <div className="feed-chart-box">
          <div className="chart-info">
            <div className="info-item">
              <span className="item-count">
                {toFixed(
                  cageSFRData.get('sfr_observed')
                    ? this.mathRound(cageSFRData.get('sfr_observed'))
                    : 0,
                )}
              </span>
              <span className="item-name">
                {formatMessage(messages.sfrObserved)}
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {toFixed(
                  cageSFRData.get('sfr_expected')
                    ? this.mathRound(cageSFRData.get('sfr_expected'))
                    : 0,
                )}
              </span>
              <span className="item-name">
                {formatMessage(messages.sfrExpected)}
              </span>
            </div>
            <div className="info-item">
              <span className="item-count">
                {cageSFRData.get('sfr_corrected')
                  ? Math.round(cageSFRData.get('sfr_corrected') * 100)
                  : 0}
                %
              </span>
              <span className="item-name">
                {formatMessage(messages.sfrCorrected)}
              </span>
            </div>
          </div>
          <div className="chart-content">
            <CircleLoader loading={chartLoader} />
            <Line
              width={null}
              height={null}
              data={this.chartData()}
              options={this.chartOptions()}
            />
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default connect(
  state => ({
    cageSFRData: state.getIn(['slaMonitor', 'cageSFRData']),
    chartLoader: state.getIn(['slaMonitor', 'cageSFRLoader']),
  }),
  {
    resetCageSFRData,
  },
)(injectIntl(CageSFRCorrected))
